import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';
import { translations } from './i18n';

const AppContext = createContext();

// Hydrate auth synchronously so pages that need the token on first render
// (e.g. a hard refresh of /order/:id) don't bounce to the login page before
// the saved session has loaded.
function loadSavedAuth() {
  try {
    const saved = localStorage.getItem('biten_auth');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (parsed?.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${parsed.token}`;
    }
    return parsed;
  } catch (e) {
    localStorage.removeItem('biten_auth');
    return null;
  }
}

export function AppProvider({ children }) {
  const [savedAuth] = useState(loadSavedAuth);
  const [user, setUser] = useState(savedAuth?.user || null);
  const [token, setToken] = useState(savedAuth?.token || null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [language, setLanguage] = useState(() => localStorage.getItem('biten_lang') || 'sv');

  useEffect(() => {
    localStorage.setItem('biten_lang', language);
  }, [language]);

  const t = useCallback((key) => {
    return translations[language]?.[key] || key;
  }, [language]);

  useEffect(() => {
    const savedCart = localStorage.getItem('biten_cart');
    if (savedCart) {
      try { setCart(JSON.parse(savedCart)); } catch (e) { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('biten_cart', JSON.stringify(cart));
  }, [cart]);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data.user);
    setToken(res.data.token);
    localStorage.setItem('biten_auth', JSON.stringify(res.data));
    localStorage.setItem('biten_test_role', 'customer');
    api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await api.post('/auth/register', data);
    setUser(res.data.user);
    setToken(res.data.token);
    localStorage.setItem('biten_auth', JSON.stringify(res.data));
    localStorage.setItem('biten_test_role', 'customer');
    api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    return res.data;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('biten_auth');
    delete api.defaults.headers.common['Authorization'];
  }, []);

  const addToCart = useCallback((item) => {
    setCart(prev => [...prev, { ...item, cartId: Date.now().toString() }]);
    setCartOpen(true);
  }, []);

  const removeFromCart = useCallback((cartId) => {
    setCart(prev => prev.filter(item => item.cartId !== cartId));
  }, []);

  const updateCartItem = useCallback((cartId, updatedItem) => {
    setCart(prev => prev.map(item => item.cartId === cartId ? { ...updatedItem, cartId } : item));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const estimateCartTotal = useCallback(() => {
    return cart.reduce((total, item) => {
      let itemPrice = item.product?.base_price || 0;
      (item.modifiers || []).forEach(mod => {
        itemPrice += (mod.price || 0) * (mod.quantity || 1);
      });
      (item.selectedOptions || []).forEach(opt => {
        itemPrice += opt.price || 0;
      });
      return total + itemPrice * item.quantity;
    }, 0);
  }, [cart]);

  return (
    <AppContext.Provider value={{
      user, token, cart, cartOpen, cartItemCount,
      setCartOpen, login, register, logout,
      addToCart, removeFromCart, updateCartItem, clearCart,
      estimateCartTotal,
      language, setLanguage, t
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
